import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { setupAmplifyPresenceSubscriptions } from '../amplify-subscriptions';
import { VJFPUser } from '../API';
import { UpdatePresencePubsub } from '../app-events';
import { PubSubEventType } from '../services/PubSub';

export function usePresence (users: VJFPUser[]) {
  const userIds = useMemo(() => users.filter(u => u).map(u => u.id).sort(), [users]);
  const prevUserIds = useRef<number[]>([]);

  // We maintain a local state of "latest updated users" to ensure re-renders when presence changes
  const [userState, setUserState] = useState<VJFPUser[]>(users);

  // Sync with props
  useEffect(() => {
    setUserState(users);
  }, [users]);

  useEffect(() => {
    // Avoid re-subscribing if only other fields in user objects changed, but IDs are the same
    if (_.isEqual(prevUserIds.current, userIds)) return;
    prevUserIds.current = userIds;

    if (userIds.length === 0) return;

    const subscription = setupAmplifyPresenceSubscriptions(userIds);

    const pubSubToken = UpdatePresencePubsub.subscribe((type, event) => {
      // Check if the event is for one of our users
      if (type === PubSubEventType.Amplify && event && userIds.includes(event.id)) {
        setUserState(currentUsers => {
          return currentUsers.map(u => {
            if (u && u.id === event.id) {
              // Only update if presence/availability actually changed
              if (u.presenceStatus !== event.presenceStatus || u.isAvailableForChat !== event.isAvailableForChat) {
                return { ...u, presenceStatus: event.presenceStatus, isAvailableForChat: event.isAvailableForChat };
              }
            }
            return u;
          });
        });
      }
    });

    return () => {
      subscription.dispose();
      pubSubToken.dispose();
    };
  }, [userIds]);

  return userState;
}
